import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import ScheduleIcon from "@material-ui/icons/Schedule";

import api from "../../services/api";

/**
 * Página pública da proposta — o que o cliente abre pelo link, sem login.
 *
 * Fica **fora** do layout autenticado de propósito: sem sidebar, sem AppBar,
 * sem nada que sugira que ele está dentro de um sistema. É um documento.
 */
const useStyles = makeStyles((theme) => ({
  page: {
    minHeight: "100vh",
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(4, 2),
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
  },
  sheet: {
    width: "100%",
    maxWidth: 760,
    padding: theme.spacing(4),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  number: {
    color: theme.palette.text.secondary,
    fontVariantNumeric: "tabular-nums",
  },
  banner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 8,
    margin: theme.spacing(3, 0),
  },
  table: { width: "100%", borderCollapse: "collapse", marginTop: theme.spacing(3) },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  td: {
    padding: theme.spacing(1.25, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 14,
  },
  num: { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  totals: {
    marginTop: theme.spacing(2),
    marginLeft: "auto",
    width: "100%",
    maxWidth: 320,
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(0.75, 0),
    fontVariantNumeric: "tabular-nums",
  },
  grandTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 0),
    borderTop: `2px solid ${theme.palette.text.primary}`,
    marginTop: theme.spacing(1),
    fontSize: 18,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  block: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    borderRadius: 8,
    whiteSpace: "pre-wrap",
    fontSize: 14,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexWrap: "wrap",
  },
  center: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: 24,
    textAlign: "center",
  },
  legal: {
    marginTop: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
}));

const money = (v, currency = "BRL") =>
  Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency });

const qty = (v) =>
  Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 3 });

const fmtDate = (d) =>
  d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR") : "";

const PublicProposal = () => {
  const classes = useStyles();
  const { token } = useParams();

  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [dialog, setDialog] = useState(null); // "accept" | "reject"
  const [name, setName] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/proposals/${token}`);
      setProposal(data);
    } catch (err) {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const respond = async (accept) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.post(`/public/proposals/${token}/respond`, {
        accept,
        name: accept ? name.trim() : undefined,
        reason: accept ? undefined : reason.trim(),
      });
      setProposal(data);
      setDialog(null);
    } catch (err) {
      setError(
        err?.response?.data?.error === "ERR_PROPOSAL_EXPIRED"
          ? "Esta proposta venceu. Entre em contato para receber uma nova."
          : "Não foi possível registrar sua resposta. Tente novamente."
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={classes.center}>
        <CircularProgress />
      </div>
    );
  }

  if (notFound || !proposal) {
    return (
      <div className={classes.center}>
        <Typography variant="h5">Proposta não encontrada</Typography>
        <Typography variant="body2" color="textSecondary">
          O link pode ter expirado ou estar incorreto. Entre em contato com quem
          enviou a proposta.
        </Typography>
      </div>
    );
  }

  const isAccepted = proposal.status === "accepted";
  const isRejected = proposal.status === "rejected";
  const isExpired = proposal.status === "expired";

  const banner = isAccepted
    ? { bg: "#F0FDF4", fg: "#15803D", icon: <CheckCircleIcon />, text: `Proposta aceita${proposal.acceptedByName ? ` por ${proposal.acceptedByName}` : ""}.` }
    : isRejected
    ? { bg: "#FEF2F2", fg: "#B91C1C", icon: <CancelIcon />, text: "Proposta recusada." }
    : isExpired
    ? { bg: "#FFFBEB", fg: "#B45309", icon: <ScheduleIcon />, text: `Esta proposta venceu em ${fmtDate(proposal.validUntil)}.` }
    : null;

  return (
    <div className={classes.page}>
      <Paper className={classes.sheet} elevation={1}>
        <div className={classes.head}>
          <div>
            <Typography variant="h4" component="h1">
              {proposal.title}
            </Typography>
            <Typography variant="body2" className={classes.number}>
              Proposta {proposal.number}
              {proposal.clientName ? ` · ${proposal.clientName}` : ""}
            </Typography>
          </div>
          {proposal.validUntil && !banner && (
            <Typography variant="body2" color="textSecondary">
              Válida até {fmtDate(proposal.validUntil)}
            </Typography>
          )}
        </div>

        {banner && (
          <div
            className={classes.banner}
            style={{ backgroundColor: banner.bg, color: banner.fg }}
          >
            {banner.icon}
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              {banner.text}
            </Typography>
          </div>
        )}

        <table className={classes.table}>
          <thead>
            <tr>
              <th className={classes.th}>Item</th>
              <th className={`${classes.th} ${classes.num}`}>Qtd</th>
              <th className={`${classes.th} ${classes.num}`}>Unitário</th>
              <th className={`${classes.th} ${classes.num}`}>Desconto</th>
              <th className={`${classes.th} ${classes.num}`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {proposal.items.map((item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}>
                <td className={classes.td}>{item.description}</td>
                <td className={`${classes.td} ${classes.num}`}>{qty(item.quantity)}</td>
                <td className={`${classes.td} ${classes.num}`}>
                  {money(item.unitPrice, proposal.currency)}
                </td>
                <td className={`${classes.td} ${classes.num}`}>
                  {item.discount > 0 ? `− ${money(item.discount, proposal.currency)}` : "—"}
                </td>
                <td className={`${classes.td} ${classes.num}`}>
                  {money(item.total, proposal.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.totals}>
          <div className={classes.totalRow}>
            <span>Subtotal</span>
            <span>{money(proposal.subtotal, proposal.currency)}</span>
          </div>
          {proposal.discountAmount > 0 && (
            <div className={classes.totalRow}>
              <span>Desconto</span>
              <span>− {money(proposal.discountAmount, proposal.currency)}</span>
            </div>
          )}
          <div className={classes.grandTotal}>
            <span>Total</span>
            <span>{money(proposal.totalAmount, proposal.currency)}</span>
          </div>
        </div>

        {proposal.notes && <div className={classes.block}>{proposal.notes}</div>}
        {proposal.terms && (
          <div className={classes.block}>
            <strong>Condições</strong>
            {"\n"}
            {proposal.terms}
          </div>
        )}

        {proposal.canRespond && (
          <>
            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<CheckCircleIcon />}
                onClick={() => setDialog("accept")}
              >
                Aceitar proposta
              </Button>
              <Button size="large" onClick={() => setDialog("reject")}>
                Recusar
              </Button>
            </div>
            <Typography className={classes.legal}>
              Ao aceitar, seu nome, data e endereço IP são registrados como
              confirmação do aceite.
            </Typography>
          </>
        )}
      </Paper>

      <Dialog open={dialog === "accept"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Aceitar proposta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Confirme seu nome para registrar o aceite de{" "}
            <strong>{money(proposal.totalAmount, proposal.currency)}</strong>.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Seu nome completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ marginTop: 12 }}
          />
          {error && (
            <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => respond(true)}
            disabled={saving || name.trim().length < 3}
          >
            {saving ? "Registrando…" : "Confirmar aceite"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialog === "reject"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Recusar proposta</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Se quiser, conte o motivo — ajuda a ajustar a proposta.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Motivo (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ marginTop: 12 }}
          />
          {error && (
            <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={saving}>
            Voltar
          </Button>
          <Button onClick={() => respond(false)} disabled={saving}>
            {saving ? "Registrando…" : "Confirmar recusa"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PublicProposal;
